import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Layout from "../../Component/Layout";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";

const fmtAmount = (val) =>
  val === null || val === undefined
    ? "—"
    : Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayStr = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// --- Shared dropdown styling (matches Home.js / Product Master look) ---
const dropdownOptionStyle = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    height: 36,
    fontSize: "13px",
    borderColor: state.isFocused ? "#94a3b8" : "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px", height: "36px", display: "flex", alignItems: "center" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "13px" }),
  singleValue: (base) => ({ ...base, fontSize: "13px", color: "#1e293b", fontWeight: 500 }),
  placeholder: (base) => ({ ...base, fontSize: "13px", color: "#94a3b8" }),
  dropdownIndicator: (base) => ({ ...base, padding: 6 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

const FONT_STACK =
  "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif";

const styles = {
  page: {
    padding: "30px 40px",
    backgroundColor: "#f8fafc",
    minHeight: "calc(100vh - 60px)",
    fontFamily: FONT_STACK,
  },
  pageTitleBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  pageTitle: {
    margin: "0 0 4px 0",
    color: "#0f172a",
    fontWeight: 700,
    fontSize: "24px",
    letterSpacing: "-0.01em",
  },
  pageSubtitle: {
    color: "#64748b",
    margin: 0,
    fontSize: "13.5px",
  },
  primaryBtn: {
    background: "#1e293b",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "0 2px 6px rgba(30,41,59,0.18)",
    transition: "background 0.15s ease",
  },
  filterCard: {
    background: "#ffffff",
    borderRadius: 10,
    padding: "20px 22px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    border: "1px solid #eef1f5",
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 6,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  tableContainer: {
    background: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    overflow: "hidden",
  },
  tableScroll: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "920px" },
  th: {
    padding: "13px 16px",
    fontSize: "11.5px",
    fontWeight: 700,
    color: "#475569",
    textAlign: "left",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    position: "sticky",
    top: 0,
  },
  td: {
    padding: "13px 16px",
    fontSize: "13.5px",
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #f1f5f9",
  },
  rowHover: { transition: "background 0.12s ease" },
  viewBtn: {
    background: "#fff",
    color: "#3b82f6",
    border: "1px solid #bfdbfe",
    borderRadius: "6px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.12s ease",
  },

  // --- Modal ---
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
    padding: "20px",
  },
  modal: {
    background: "#fff",
    borderRadius: "14px",
    width: "100%",
    maxWidth: "820px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
    fontFamily: FONT_STACK,
  },
  modalHeader: {
    padding: "18px 24px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { fontSize: "17px", fontWeight: 700, color: "#0f172a", margin: 0 },
  closeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    color: "#94a3b8",
    lineHeight: 1,
  },
  modalBody: { padding: "22px 24px", overflowY: "auto", flex: 1 },
  modalRow: { display: "flex", gap: "16px", marginBottom: "18px" },
  modalField: { flex: 1, minWidth: "150px" },
  requiredMark: { color: "#ef4444", marginLeft: "3px" },
  textInput: {
    width: "100%",
    height: "36px",
    fontSize: "13px",
    padding: "0 10px",
    border: "1px solid #dee2e6",
    borderRadius: "6px",
    outline: "none",
    boxSizing: "border-box",
    color: "#1e293b",
  },
  errorText: { fontSize: "11.5px", color: "#ef4444", marginTop: "4px" },
  modalFooter: {
    padding: "16px 24px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cancelBtn: {
    background: "#fff",
    color: "#334155",
    border: "1px solid #dee2e6",
    borderRadius: "8px",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    marginRight: "10px",
  },
  saveBtn: {
    background: "#1e293b",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "9px 20px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

/* =====================================================================
   Small reusable field components
   Every field in Create + View modals is one of these shapes, so both
   modals can be composed from the same building blocks below.
   ===================================================================== */

function FieldWrapper({ label, required, error, flexFull, children }) {
  return (
    <div style={{ ...styles.modalField, ...(flexFull ? { flex: "1 1 100%" } : {}) }}>
      <label style={styles.filterLabel}>
        {label}
        {required && <span style={styles.requiredMark}>*</span>}
      </label>
      {children}
      {error && <div style={styles.errorText}>{error}</div>}
    </div>
  );
}

function TextField({ label, required, error, value, onChange, placeholder, type = "text", step, flexFull }) {
  return (
    <FieldWrapper label={label} required={required} error={error} flexFull={flexFull}>
      <input
        type={type}
        step={step}
        style={styles.textInput}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldWrapper>
  );
}

/* dd-mm-yyyy date input. The form keeps the value as yyyy-mm-dd (what the
   API/DB expect); the user only ever sees and types dd-mm-yyyy. The calendar
   button opens the native picker as a convenience. */
const isoToDisplay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

const displayToIso = (txt) => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(txt);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  const valid =
    d.getFullYear() === Number(yyyy) &&
    d.getMonth() === Number(mm) - 1 &&
    d.getDate() === Number(dd);
  return valid ? `${yyyy}-${mm}-${dd}` : "";
};

function DateField({ label, required, error, value, onChange }) {
  const [text, setText] = useState(isoToDisplay(value));
  const pickerRef = useRef(null);

  // Sync when the value is changed from outside (e.g. record loaded in View modal)
  useEffect(() => {
    if (displayToIso(text) !== value) setText(isoToDisplay(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleType = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}-${digits.slice(2)}`;
    setText(out);
    onChange(displayToIso(out));
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (el.showPicker) el.showPicker();
    else el.click();
  };

  const incomplete = text.length > 0 && !displayToIso(text);

  return (
    <FieldWrapper
      label={label}
      required={required}
      error={error || (incomplete && text.length === 10 ? "Enter a valid date (dd-mm-yyyy)" : "")}
    >
      <div style={{ position: "relative" }}>
        <input
          type="text"
          inputMode="numeric"
          style={{ ...styles.textInput, paddingRight: "34px" }}
          placeholder="dd-mm-yyyy"
          maxLength={10}
          value={text}
          onChange={(e) => handleType(e.target.value)}
        />
        <button
          type="button"
          onClick={openPicker}
          title="Pick a date"
          style={{
            position: "absolute", right: 4, top: 4, height: 28, width: 28,
            border: "none", background: "transparent", cursor: "pointer", fontSize: 14,
          }}
        >
          📅
        </button>
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          value={value || ""}
          onChange={(e) => {
            if (e.target.value) {
              setText(isoToDisplay(e.target.value));
              onChange(e.target.value);
            }
          }}
          style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        />
      </div>
    </FieldWrapper>
  );
}

function SelectField({ label, required, error, value, onChange, options, placeholder, isClearable = true, isDisabled = false }) {
  return (
    <FieldWrapper label={label} required={required} error={error}>
      <Select
        options={options}
        value={options.find((o) => o.value === value) || null}
        onChange={(opt) => onChange(opt ? opt.value : "")}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={dropdownOptionStyle}
      />
    </FieldWrapper>
  );
}

/* =====================================================================
   Shared Account Posting form modal
   Used for BOTH "Create" and "View / Edit" flows.
   - Both show Posting Date, Amount, Currency, Supplier, Reason for Payment
   - View additionally has an initial fetch-loading state
   ===================================================================== */

function PostingFormModal({
  title,
  form,
  errors,
  onFieldChange,
  onClose,
  onSave,
  saving,
  loading,
  saveLabel,
  glCategoryOptions,
  glTypeOptions,
  glCodeOptions,
  locationOptions,
  currencyOptions,
  companyCodeOptions,
  supplierOptions,
}) {
  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{title}</h3>
          <button style={styles.closeBtn} onClick={onClose}>✖</button>
        </div>

        {/* Body */}
        <div style={styles.modalBody}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
              Loading details...
            </div>
          ) : (
            <>
              {/* Row 1: Company Code, Location */}
              <div style={styles.modalRow}>
                <SelectField
                  label="Company Code"
                  required
                  placeholder="Select company code"
                  options={companyCodeOptions}
                  value={form.companyCode}
                  onChange={(v) => onFieldChange("companyCode", v)}
                  error={errors.companyCode}
                />
                <SelectField
                  label="Location"
                  placeholder="Select location"
                  options={locationOptions}
                  value={form.location}
                  onChange={(v) => onFieldChange("location", v)}
                />
              </div>

              {/* Row 2: GL Category, GL Type, GL Code */}
              <div style={styles.modalRow}>
                <SelectField
                  label="GL Category"
                  placeholder="Select"
                  options={glCategoryOptions}
                  value={form.glCategory}
                  onChange={(v) => onFieldChange("glCategory", v)}
                />
                <SelectField
                  label="GL Type"
                  placeholder="Select"
                  options={glTypeOptions}
                  value={form.glType}
                  onChange={(v) => onFieldChange("glType", v)}
                />
                <SelectField
                  label="GL Code"
                  required
                  placeholder={
                    form.glCategory && form.glType
                      ? glCodeOptions.length
                        ? "Select GL code"
                        : "No GL code configured for this combo"
                      : "Select GL Category & Type first"
                  }
                  options={glCodeOptions}
                  value={form.glCode}
                  onChange={(v) => onFieldChange("glCode", v)}
                  error={errors.glCode}
                  isDisabled={!form.glCategory || !form.glType}
                />
              </div>

              {/* Row 3: Description */}
              <div style={styles.modalRow}>
                <TextField
                  label="Description"
                  flexFull
                  placeholder="Enter description"
                  value={form.description}
                  onChange={(v) => onFieldChange("description", v)}
                />
              </div>

              {/* Row 4: Posting Date, Amount, Currency */}
              <div style={styles.modalRow}>
                <DateField
                  label="Posting Date"
                  required
                  value={form.postingDate}
                  onChange={(v) => onFieldChange("postingDate", v)}
                  error={errors.postingDate}
                />
                <TextField
                  label="Amount"
                  required
                  type="number"
                  step="0.01"
                  placeholder="Enter amount"
                  value={form.amount}
                  onChange={(v) => onFieldChange("amount", v)}
                  error={errors.amount}
                />
                <SelectField
                  label="Currency"
                  placeholder="Select currency"
                  options={currencyOptions}
                  value={form.currency}
                  onChange={(v) => onFieldChange("currency", v || "INR")}
                  isClearable={false}
                />
              </div>

              {/* Row 5: Supplier, Reason for Payment */}
              <div style={styles.modalRow}>
                <SelectField
                  label="Supplier"
                  placeholder="Select supplier"
                  options={supplierOptions}
                  value={form.supplier}
                  onChange={(v) => onFieldChange("supplier", v)}
                />
                <TextField
                  label="Reason for Payment"
                  placeholder="Enter reason for payment"
                  value={form.reasonForPayment}
                  onChange={(v) => onFieldChange("reasonForPayment", v)}
                />
              </div>

              {errors.submit && (
                <div style={{ ...styles.errorText, fontSize: "13px", marginTop: "10px" }}>
                  {errors.submit}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ ...styles.modalFooter, justifyContent: "flex-end" }}>
          <button style={styles.cancelBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button style={styles.saveBtn} onClick={onSave} disabled={saving || loading}>
            {saving ? "Saving..." : saveLabel}
          </button>
        </div>

      </div>
    </div>
  );
}

const emptyForm = () => ({
  companyCode: "",
  location: "",
  glCategory: "",
  glType: "",
  glCode: "",
  description: "",
  postingDate: todayStr(),
  amount: "",
  currency: "INR",
  supplier: "",
  reasonForPayment: "",
});

function AccountPosting() {
  const [filterOptions, setFilterOptions] = useState({
    glCategories: [],
    glTypes: [],
    glCodes: [],
  });

  // GL Category / GL Type now come from the Configuration master
  // (same source as Product Master's "GL Category" / "GL Type" screens),
  // instead of the old account-posting/filters endpoint.
  const [glMasterOptions, setGlMasterOptions] = useState({
    glCategories: [],
    glTypes: [],
  });

  const [filters, setFilters] = useState({
    glCategory: "",
    glType: "",
    glCode: "",
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);

  const [masterOptions, setMasterOptions] = useState({
    locations: [], // [{ id, locationName }]
    currencies: [], // ["INR", "USD", ...]
    suppliers: [], // [{ value: partner_code, label: "name - code" }]
  });

  // Company Code options — from the Configuration > Company Code master table
  const [companyCodeOptions, setCompanyCodeOptions] = useState([]);

  // GL Code options fetched for the currently selected GL Category + GL Type
  // (Create modal and View modal each get their own, since both can be open
  // with different category/type combos).
  const [createGlCodeChoices, setCreateGlCodeChoices] = useState([]);
  const [viewGlCodeChoices, setViewGlCodeChoices] = useState([]);

  // --- Create Modal state ---
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // --- View / Edit Modal state ---
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [viewForm, setViewForm] = useState(emptyForm);
  const [viewErrors, setViewErrors] = useState({});
  const [viewSaving, setViewSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${PURCHASE_API}/account-posting/filters`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((err) => {
        console.error("ACCOUNT POSTING FILTERS ERROR:", err);
      });
  }, []);

  // Load GL Category & GL Type from Configuration master
  useEffect(() => {
    axios
      .get(`${MASTER_API}/configuration/dynamic/gl-category`)
      .then((res) => {
        const list = (res.data || []).map((r) => r.name);
        setGlMasterOptions((prev) => ({ ...prev, glCategories: list }));
      })
      .catch((err) => {
        console.error("GL CATEGORY FETCH ERROR:", err);
      });

    axios
      .get(`${MASTER_API}/configuration/dynamic/gl-type`)
      .then((res) => {
        const list = (res.data || []).map((r) => r.name);
        setGlMasterOptions((prev) => ({ ...prev, glTypes: list }));
      })
      .catch((err) => {
        console.error("GL TYPE FETCH ERROR:", err);
      });
  }, []);

  // Load Location, Currency & Supplier master data once
  useEffect(() => {
    axios
      .get(`${PURCHASE_API}/account-posting/master-data`)
      .then((res) =>
        setMasterOptions({
          locations: res.data?.locations || [],
          currencies: res.data?.currencies || [],
          suppliers: res.data?.suppliers || [],
        })
      )
      .catch((err) => {
        console.error("ACCOUNT POSTING MASTER DATA ERROR:", err);
      });
  }, []);

  // Load Company Code options from the Configuration > Company Code master table
  useEffect(() => {
    axios
      .get(`${MASTER_API}/configuration/company-code`)
      .then((res) => {
        const list = (res.data || []).map((r) => ({
          value: r.company_code,
          label: r.company_code,
        }));
        setCompanyCodeOptions(list);
      })
      .catch((err) => {
        console.error("COMPANY CODE FETCH ERROR:", err);
      });
  }, []);

  // Auto-fetch GL Codes for the Create form whenever GL Category + GL Type are both set
  useEffect(() => {
    if (!form.glCategory || !form.glType) {
      setCreateGlCodeChoices([]);
      return;
    }
    axios
      .get(`${PURCHASE_API}/account-posting/gl-codes`, {
        params: { glCategory: form.glCategory, glType: form.glType },
      })
      .then((res) => {
        const codes = res.data?.glCodes || [];
        setCreateGlCodeChoices(codes);
        // If only one GL Code is configured for this combo, select it automatically
        if (codes.length === 1) {
          setForm((prev) => ({ ...prev, glCode: codes[0] }));
        } else if (!codes.includes(form.glCode)) {
          // clear a stale selection that doesn't belong to the new combo
          setForm((prev) => ({ ...prev, glCode: "" }));
        }
      })
      .catch((err) => {
        console.error("GL CODES (CREATE) FETCH ERROR:", err);
        setCreateGlCodeChoices([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.glCategory, form.glType]);

  // Auto-fetch GL Codes for the View/Edit form whenever GL Category + GL Type are both set
  useEffect(() => {
    if (!viewForm.glCategory || !viewForm.glType) {
      setViewGlCodeChoices([]);
      return;
    }
    axios
      .get(`${PURCHASE_API}/account-posting/gl-codes`, {
        params: { glCategory: viewForm.glCategory, glType: viewForm.glType },
      })
      .then((res) => {
        const codes = res.data?.glCodes || [];
        // Keep the record's existing GL Code in the list even if it's a legacy
        // value that isn't (or no longer is) configured under this combo.
        const merged =
          viewForm.glCode && !codes.includes(viewForm.glCode)
            ? [...codes, viewForm.glCode]
            : codes;
        setViewGlCodeChoices(merged);
      })
      .catch((err) => {
        console.error("GL CODES (VIEW) FETCH ERROR:", err);
        setViewGlCodeChoices([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewForm.glCategory, viewForm.glType]);

  const glCategoryOptions = useMemo(
    () => (glMasterOptions.glCategories || []).map((c) => ({ value: c, label: c })),
    [glMasterOptions.glCategories]
  );

  const glTypeOptions = useMemo(
    () => (glMasterOptions.glTypes || []).map((t) => ({ value: t, label: t })),
    [glMasterOptions.glTypes]
  );

  const glCodeOptions = useMemo(
    () =>
      (filterOptions.glCodes || []).map((g) => ({
        value: g.gl_code,
        label: g.description ? `${g.gl_code} — ${g.description}` : g.gl_code,
      })),
    [filterOptions.glCodes]
  );

  // GL Code dropdown options for the Create / View modals, driven by the
  // Category+Type combo currently selected in each form.
  const createGlCodeOptions = useMemo(
    () => createGlCodeChoices.map((c) => ({ value: c, label: c })),
    [createGlCodeChoices]
  );
  const viewGlCodeOptions = useMemo(
    () => viewGlCodeChoices.map((c) => ({ value: c, label: c })),
    [viewGlCodeChoices]
  );

  const locationOptions = useMemo(
    () =>
      (masterOptions.locations || []).map((l) => ({
        value: l.locationName,
        label: l.locationName,
      })),
    [masterOptions.locations]
  );

  const currencyOptions = useMemo(
    () => (masterOptions.currencies || []).map((c) => ({ value: c, label: c })),
    [masterOptions.currencies]
  );

  // Supplier options come from Business Partner (partner_type = Supplier).
  // value = partner_code, label = "business_partner_name - partner_code"
  const supplierOptions = useMemo(
    () => masterOptions.suppliers || [],
    [masterOptions.suppliers]
  );

  // In the View modal, keep a legacy free-text supplier visible even if it
  // isn't a partner_code in the list (older records saved before this dropdown).
  const viewSupplierOptions = useMemo(() => {
    const current = viewForm.supplier;
    if (current && !supplierOptions.some((o) => o.value === current)) {
      return [...supplierOptions, { value: current, label: current }];
    }
    return supplierOptions;
  }, [supplierOptions, viewForm.supplier]);

  const fetchPostings = (activeFilters) => {
    setLoading(true);
    setError("");

    const params = {};
    if (activeFilters.glCategory) params.glCategory = activeFilters.glCategory;
    if (activeFilters.glType) params.glType = activeFilters.glType;
    if (activeFilters.glCode) params.glCode = activeFilters.glCode;

    axios
      .get(`${PURCHASE_API}/account-posting`, { params })
      .then((res) => {
        setRows(res.data?.rows || []);
      })
      .catch((err) => {
        console.error("ACCOUNT POSTING FETCH ERROR:", err);
        setError("Failed to load account postings. Please try again.");
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPostings(filters);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /* ---------------- Create modal handlers ---------------- */

  const handleCreate = () => {
    setForm(emptyForm());
    setFormErrors({});
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (saving) return;
    setShowCreateModal(false);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!form.companyCode) errs.companyCode = "Company Code is required";
    if (!form.glCode) errs.glCode = "GL Code is required";
    if (!form.postingDate) errs.postingDate = "Posting Date is required";
    if (form.amount === "" || form.amount === null || isNaN(Number(form.amount))) {
      errs.amount = "Enter a valid amount";
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    setSaving(true);
    axios
      .post(`${PURCHASE_API}/account-posting`, { ...form, amount: Number(form.amount) })
      .then(() => {
        setShowCreateModal(false);
        fetchPostings(filters);
      })
      .catch((err) => {
        console.error("ACCOUNT POSTING SAVE ERROR:", err);
        const msg = err.response?.data?.message || "Failed to save. Please try again.";
        setFormErrors((prev) => ({ ...prev, submit: msg }));
      })
      .finally(() => setSaving(false));
  };

  /* ---------------- View / Edit modal handlers ---------------- */

  const handleView = (row) => {
    const id = row.id;
    setViewId(id);
    setViewErrors({});
    setShowViewModal(true);
    setViewLoading(true);

    axios
      .get(`${PURCHASE_API}/account-posting/${id}`)
      .then((res) => {
        const data = res.data || {};
        setViewForm({
          companyCode: data.companyCode || "",
          location: data.location || "",
          glCategory: data.glCategory || "",
          glType: data.glType || "",
          glCode: data.glCode || "",
          description: data.description || "",
          postingDate: data.postingDate || todayStr(),
          amount: data.amount ?? "",
          currency: data.currency || "INR",
          supplier: data.supplier || "",
          reasonForPayment: data.reasonForPayment || "",
        });
      })
      .catch((err) => {
        console.error("ACCOUNT POSTING VIEW FETCH ERROR:", err);
        setViewErrors({ submit: "Failed to load record details." });
      })
      .finally(() => setViewLoading(false));
  };

  const closeViewModal = () => {
    if (viewSaving) return;
    setShowViewModal(false);
    setViewId(null);
  };

  const handleViewFormChange = (field, value) => {
    setViewForm((prev) => ({ ...prev, [field]: value }));
    if (viewErrors[field]) {
      setViewErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateViewForm = () => {
    const errs = {};
    if (!viewForm.companyCode) errs.companyCode = "Company Code is required";
    if (!viewForm.glCode) errs.glCode = "GL Code is required";
    if (!viewForm.postingDate) errs.postingDate = "Posting Date is required";
    if (viewForm.amount === "" || viewForm.amount === null || isNaN(Number(viewForm.amount))) {
      errs.amount = "Enter a valid amount";
    }
    setViewErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleViewSave = () => {
    if (!validateViewForm()) return;

    setViewSaving(true);
    axios
      .put(`${PURCHASE_API}/account-posting/${viewId}`, {
        ...viewForm,
        amount: Number(viewForm.amount),
      })
      .then(() => {
        setShowViewModal(false);
        setViewId(null);
        fetchPostings(filters);
      })
      .catch((err) => {
        console.error("ACCOUNT POSTING UPDATE ERROR:", err);
        const msg =
          err.response?.data?.message || "Failed to update account posting. Please try again.";
        setViewErrors((prev) => ({ ...prev, submit: msg }));
      })
      .finally(() => setViewSaving(false));
  };

  return (
    <Layout>
      <div style={styles.page}>

        {/* Page Header */}
        <div style={styles.pageTitleBar}>
          <div>
            <h2 style={styles.pageTitle}>Account Posting</h2>
            <p style={styles.pageSubtitle}>
              View and manage General Ledger account postings
            </p>
          </div>

          <button
            style={styles.primaryBtn}
            onClick={handleCreate}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#334155")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#1e293b")}
          >
            + Create
          </button>
        </div>

        {/* Filter Card */}
        <div style={styles.filterCard}>
          <div style={{ display: "flex", gap: "18px", flexWrap: "nowrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={styles.filterLabel}>GL Category</label>
              <Select
                options={glCategoryOptions}
                value={glCategoryOptions.find((o) => o.value === filters.glCategory) || null}
                onChange={(opt) => handleChange("glCategory", opt ? opt.value : "")}
                placeholder="Select category"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={styles.filterLabel}>GL Type</label>
              <Select
                options={glTypeOptions}
                value={glTypeOptions.find((o) => o.value === filters.glType) || null}
                onChange={(opt) => handleChange("glType", opt ? opt.value : "")}
                placeholder="Select type"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={styles.filterLabel}>GL Code</label>
              <Select
                options={glCodeOptions}
                value={glCodeOptions.find((o) => o.value === filters.glCode) || null}
                onChange={(opt) => handleChange("glCode", opt ? opt.value : "")}
                placeholder="Select code"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dropdownOptionStyle}
              />
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
            borderRadius: "10px", padding: "12px 16px", marginBottom: "18px", fontSize: "13px"
          }}>
            {error}
          </div>
        )}

        {/* Postings Table */}
        <div style={styles.tableContainer}>
          <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>GL Category</th>
                  <th style={styles.th}>GL Type</th>
                  <th style={styles.th}>GL Code</th>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Amount</th>
                  <th style={{ ...styles.th, textAlign: "center" }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ ...styles.td, textAlign: "center", padding: "48px", color: "#94a3b8" }}>
                      Loading account postings...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...styles.td, textAlign: "center", padding: "48px", color: "#94a3b8" }}>
                      No account postings found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const key = row.id || idx;
                    const isHovered = hoveredRow === key;
                    return (
                      <tr
                        key={key}
                        style={{
                          background: isHovered ? "#f8fafc" : idx % 2 === 0 ? "#ffffff" : "#fcfcfd",
                          ...styles.rowHover,
                        }}
                        onMouseEnter={() => setHoveredRow(key)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <td style={styles.td}>{row.glCategory || "—"}</td>
                        <td style={styles.td}>{row.glType || "—"}</td>
                        <td style={styles.td}>{row.glCode || "—"}</td>
                        <td style={{ ...styles.td, whiteSpace: "normal", color: "#475569" }}>
                          {row.description || "—"}
                        </td>
                        <td style={{ ...styles.td, textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                          {fmtAmount(row.amount)}
                        </td>
                        <td style={{ ...styles.td, textAlign: "center" }}>
                          <button
                            style={styles.viewBtn}
                            onClick={() => handleView(row)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#eff6ff";
                              e.currentTarget.style.borderColor = "#93c5fd";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#fff";
                              e.currentTarget.style.borderColor = "#bfdbfe";
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Account Posting Modal */}
        {showCreateModal && (
          <PostingFormModal
            title="Create Account Posting"
            form={form}
            errors={formErrors}
            onFieldChange={handleFormChange}
            onClose={closeCreateModal}
            onSave={handleSave}
            saving={saving}
            loading={false}
            saveLabel="Save"
            glCategoryOptions={glCategoryOptions}
            glTypeOptions={glTypeOptions}
            glCodeOptions={createGlCodeOptions}
            locationOptions={locationOptions}
            currencyOptions={currencyOptions}
            companyCodeOptions={companyCodeOptions}
            supplierOptions={supplierOptions}
          />
        )}

        {/* View / Edit Account Posting Modal */}
        {showViewModal && (
          <PostingFormModal
            title="Account Posting Details"
            form={viewForm}
            errors={viewErrors}
            onFieldChange={handleViewFormChange}
            onClose={closeViewModal}
            onSave={handleViewSave}
            saving={viewSaving}
            loading={viewLoading}
            saveLabel="Update"
            glCategoryOptions={glCategoryOptions}
            glTypeOptions={glTypeOptions}
            glCodeOptions={viewGlCodeOptions}
            locationOptions={locationOptions}
            currencyOptions={currencyOptions}
            companyCodeOptions={companyCodeOptions}
            supplierOptions={viewSupplierOptions}
          />
        )}

      </div>
    </Layout>
  );
}

export default AccountPosting;